'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

interface NavItem {
  label: string;
  href: string;
  icon: string;
  badge?: number;
  badgeType?: 'alert' | 'info' | 'live';
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Analytics',
    items: [
      { label: 'Dashboard', href: '/', icon: 'ChartBarIcon' },
      { label: 'Matchs du Jour', href: '/matchs-du-jour', icon: 'CalendarDaysIcon', badge: 7, badgeType: 'info' },
      { label: 'Analyse des Équipes', href: '/analyse-des-quipes', icon: 'MagnifyingGlassCircleIcon' },
      { label: 'Tendances', href: '/tendances-statistiques', icon: 'ArrowTrendingUpIcon' },
      { label: 'Historique', href: '/historique-des-projections', icon: 'ClockIcon' },
    ],
  },
  {
    label: 'Go-Live',
    items: [
      { label: 'Onboarding', href: '/onboarding', icon: 'RocketLaunchIcon' },
      { label: 'Trust & Transparency', href: '/trust-dashboard', icon: 'ShieldCheckIcon' },
      { label: 'System Health', href: '/health', icon: 'ServerIcon', badge: 1, badgeType: 'alert' },
      { label: 'Launch Page', href: '/launch', icon: 'StarIcon' },
      { label: 'Landing Page', href: '/landing', icon: 'GlobeAltIcon' },
    ],
  },
  {
    label: 'Phase Quant',
    items: [
      { label: 'Adaptive Learning', href: '/adaptive-learning', icon: 'AcademicCapIcon', badgeType: 'live' },
      { label: 'Meta Model', href: '/meta-model', icon: 'CircleStackIcon' },
      { label: 'Proprietary Signals', href: '/proprietary-signals', icon: 'LockClosedIcon', badge: 7, badgeType: 'info' },
      { label: 'Behavior Analytics', href: '/behavior-analytics', icon: 'UserCircleIcon' },
      { label: 'Research Lab', href: '/research-lab', icon: 'BeakerIcon' },
      { label: 'Decision Engine', href: '/decision-engine', icon: 'BoltIcon', badge: 3, badgeType: 'alert' },
      { label: 'Knowledge Graph', href: '/knowledge-graph', icon: 'ShareIcon' },
      { label: 'AI Agents', href: '/autonomous-agents', icon: 'CpuChipIcon', badge: 6, badgeType: 'live' },
      { label: 'Quant Terminal', href: '/quant-terminal', icon: 'CommandLineIcon', badgeType: 'live' },
      { label: 'Data Moat', href: '/data-moat', icon: 'ServerIcon' },
    ],
  },
  {
    label: 'Phase Elite',
    items: [
      { label: 'Analytics Copilot', href: '/analytics-copilot', icon: 'SparklesIcon', badgeType: 'live' },
      { label: 'SimulationLab', href: '/simulation-lab', icon: 'BeakerIcon' },
      { label: 'Player Intelligence', href: '/player-intelligence', icon: 'UserGroupIcon' },
      { label: 'Market Intelligence', href: '/market-intelligence', icon: 'ChartBarIcon', badge: 4, badgeType: 'alert' },
      { label: 'AI Sentinel', href: '/ai-sentinel', icon: 'ShieldCheckIcon', badge: 2, badgeType: 'alert' },
    ],
  },
  {
    label: 'Visualisation',
    items: [
      { label: 'Viz Engine', href: '/viz-engine', icon: 'PresentationChartBarIcon' },
      { label: 'Community', href: '/community', icon: 'UsersIcon' },
      { label: 'Custom Models', href: '/custom-models', icon: 'CpuChipIcon' },
      { label: 'Multi-Sport', href: '/multi-sport', icon: 'GlobeAltIcon' },
    ],
  },
  {
    label: 'Moteur ML',
    items: [
      { label: 'Données Historiques', href: '/historique-donnees', icon: 'CircleStackIcon' },
      { label: 'ML Engine', href: '/ml-engine', icon: 'CpuChipIcon' },
      { label: 'AutoTraining', href: '/auto-training', icon: 'ArrowPathIcon' },
      { label: 'Backtesting', href: '/backtesting', icon: 'BeakerIcon' },
    ],
  },
  {
    label: 'Production',
    items: [
      { label: 'Data Pipeline', href: '/pipeline', icon: 'ServerIcon', badge: 5, badgeType: 'live' },
      { label: 'Projections Avancées', href: '/projections-avancees', icon: 'SparklesIcon' },
      { label: 'Event Detection', href: '/event-detection', icon: 'BoltIcon', badge: 5, badgeType: 'alert' },
    ],
  },
  {
    label: 'Temps Réel',
    items: [
      { label: 'Live Analytics', href: '/live-analytics', icon: 'SignalIcon', badge: 3, badgeType: 'live' },
      { label: 'Alertes', href: '/alertes', icon: 'BellAlertIcon', badge: 3, badgeType: 'alert' },
      { label: 'AI Insights', href: '/ai-insights', icon: 'LightBulbIcon' },
    ],
  },
  {
    label: 'SaaS Platform',
    items: [
      { label: 'Monétisation', href: '/monetisation', icon: 'CurrencyDollarIcon' },
      { label: 'API Platform', href: '/api-platform', icon: 'CodeBracketIcon' },
      { label: 'DevOps', href: '/devops', icon: 'ShieldCheckIcon' },
      { label: 'Business Intel.', href: '/business-intelligence', icon: 'PresentationChartBarIcon' },
    ],
  },
  {
    label: 'Compte',
    items: [
      { label: 'Personnalisation', href: '/personnalisation', icon: 'AdjustmentsHorizontalIcon' },
      { label: 'Profil', href: '/profil-utilisateur', icon: 'UserCircleIcon' },
    ],
  },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  return (
    <aside
      className={`sidebar-transition flex flex-col h-screen bg-card border-r border-border fixed left-0 top-0 z-40 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center border-b border-border h-14 px-3 ${collapsed ? 'justify-center' : 'gap-2'}`}>
        <AppLogo size={28} />
        {!collapsed && (
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold text-foreground leading-tight truncate">NHL Analytics</span>
            <span className="text-2xs text-primary font-mono-data tracking-wider">PRO · PRODUCTION</span>
          </div>
        )}
      </div>

      {/* Live indicator */}
      {!collapsed && (
        <div className="mx-3 mt-3 mb-1 flex items-center gap-2 px-2 py-1.5 rounded bg-positive-subtle border border-positive/20">
          <span className="w-1.5 h-1.5 rounded-full bg-positive pulse-dot flex-shrink-0" />
          <span className="text-2xs font-medium text-positive font-mono-data tracking-wide">LIVE DATA — NHL 2025–26</span>
        </div>
      )}

      {/* Nav groups */}
      <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            {!collapsed && (
              <p className="text-2xs font-medium tracking-widest uppercase text-muted-foreground px-3 py-2 mt-2">
                {group.label}
              </p>
            )}
            {group.items.map((item) => {
              const active = isActive(item.href);
              return (
                <Link
                  key={`nav-${item.href}`}
                  href={item.href}
                  title={collapsed ? item.label : undefined}
                  className={`nav-item ${active ? 'nav-item-active' : ''} ${collapsed ? 'justify-center px-0' : ''}`}
                >
                  <Icon
                    name={item.icon as Parameters<typeof Icon>[0]['name']}
                    size={18}
                    className={active ? 'text-primary' : 'text-muted-foreground'}
                  />
                  {!collapsed && (
                    <span className="flex-1 text-sm">{item.label}</span>
                  )}
                  {!collapsed && item.badge && (
                    <span
                      className={`status-badge text-2xs px-1.5 py-0.5 ${
                        item.badgeType === 'alert' ? 'bg-negative-subtle text-negative'
                          : item.badgeType === 'live' ? 'bg-positive-subtle text-positive' : 'bg-info-subtle text-primary'
                      }`}
                    >
                      {item.badgeType === 'live' ? '●' : ''}{item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Enterprise badge */}
      {!collapsed && (
        <div className="mx-3 mb-3 p-3 rounded-lg bg-gradient-to-br from-primary/10 to-purple-500/10 border border-primary/20">
          <div className="flex items-center gap-2 mb-1.5">
            <Icon name="RocketLaunchIcon" size={14} className="text-primary" />
            <span className="text-xs font-semibold text-primary">SaaS Production</span>
          </div>
          <p className="text-2xs text-muted-foreground leading-relaxed">Pipeline · AutoML · API · DevOps · BI</p>
        </div>
      )}

      {/* Collapse toggle */}
      <div className="border-t border-border p-2">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`btn-ghost w-full flex items-center gap-2 ${collapsed ? 'justify-center' : ''}`}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon
            name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'}
            size={16}
            className="text-muted-foreground"
          />
          {!collapsed && <span className="text-xs text-muted-foreground">Réduire</span>}
        </button>
      </div>
    </aside>
  );
}